import React, { useEffect, useState } from 'react'
import { adminApi } from '../services/api'
import { Loader } from '../components/UI'
import { toFaDigits } from '../utils/format'
import toast from 'react-hot-toast'

const TYPES = ['GOLD', 'SILVER']
const CATEGORIES: Array<[string, string]> = [
  ['SHMESH', 'شمش'],
  ['ABSHADE', 'آب‌شده'],
  ['COIN', 'سکه'],
  ['BAR', 'بار'],
]

const TYPE_FA: Record<string, string> = { GOLD: 'طلا', SILVER: 'نقره' }

const Markups: React.FC = () => {
  const [defaults, setDefaults] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [applying, setApplying] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const res = await adminApi.getDefaultMarkups()
      if (res.data.success && res.data.data) setDefaults(res.data.data)
    } catch {
      toast.error('خطا در دریافت درصد سودها')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const keyOf = (type: string, cat: string) => `${type}.${cat}`

  const save = async (type: string, cat: string) => {
    const key = keyOf(type, cat)
    const markup = defaults[key]
    if (markup === undefined) return
    setSaving(key)
    try {
      const res = await adminApi.updateDefaultMarkup(type, cat, markup)
      if (res.data.success) toast.success(`درصد سود ذخیره شد`)
    } catch {
      toast.error('خطا در ذخیره')
    } finally {
      setSaving(null)
    }
  }

  const bulkSave = async () => {
    setSaving('all')
    try {
      const res = await adminApi.bulkUpdateMarkups(defaults)
      if (res.data.success) toast.success('همه درصد سودها ذخیره شدند')
    } catch {
      toast.error('خطا در ذخیره')
    } finally {
      setSaving(null)
    }
  }

  const applyToProducts = async () => {
    setApplying(true)
    try {
      const res = await adminApi.applyMarkupsToProducts()
      toast.success(`${toFaDigits(res.data.data?.updated || 0)} محصول به‌روزرسانی شد`)
    } catch {
      toast.error('خطا در اعمال درصد سود روی محصولات')
    } finally {
      setApplying(false)
    }
  }

  if (loading) return <div className="py-20 flex justify-center"><Loader /></div>

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-gray-900">درصد سود طلا و نقره</h1>
          <p className="text-sm text-gray-500 mt-1">
            قیمت فروش = قیمت خام × (۱ + درصد سود). این درصدها روی همه محصولات هر دسته قابل اعمال است.
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-outline" onClick={applyToProducts} disabled={applying}>
            {applying ? 'در حال اعمال…' : 'اعمال روی همه محصولات'}
          </button>
          <button className="btn btn-primary" onClick={bulkSave} disabled={saving === 'all'}>
            {saving === 'all' ? 'در حال ذخیره…' : 'ذخیره همه'}
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {TYPES.map((type) => (
          <div key={type} className="card overflow-hidden">
            <div className={`px-5 py-4 border-b font-black text-white ${type === 'GOLD' ? 'bg-gold-600' : 'bg-gray-600'}`}>
              {TYPE_FA[type]} — درصد سود پیش‌فرض بر اساس دسته
            </div>
            <div className="divide-y divide-gray-50">
              {CATEGORIES.map(([cat, label]) => {
                const key = keyOf(type, cat)
                const val = defaults[key] ?? 0
                const savingKey = saving === key
                return (
                  <div key={key} className="flex items-center justify-between gap-4 px-5 py-3.5">
                    <div>
                      <div className="font-bold text-gray-800">{label}</div>
                      <div className="text-xs text-gray-400">این درصد روی محصولات {label} اعمال می‌شود</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-1">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          className="w-16 bg-transparent text-left font-bold outline-none"
                          dir="ltr"
                          value={val}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value)
                            setDefaults((p) => ({ ...p, [key]: isNaN(v) ? 0 : v }))
                          }}
                        />
                        <span className="text-xs">٪</span>
                      </div>
                      <button className="btn btn-primary !py-1.5 !px-3" onClick={() => save(type, cat)} disabled={!!saving}>
                        {savingKey ? '…' : 'ذخیره'}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="card p-5 text-sm text-gray-600 leading-7">
        <b className="text-gray-900">نکته:</b>
        <br />• هر محصول می‌تواند درصد سود اختصاصی خودش را داشته باشد (از صفحه «محصولات» قابل تغییر است).
        <br />• با دکمه «اعمال روی همه محصولات»، درصد پیش‌فرض هر دسته روی همه محصولات آن دسته اعمال می‌شود.
        <br />• بعد از تغییر درصد سود، قیمت نهایی بلافاصله محاسبه و ذخیره می‌شود.
      </div>
    </div>
  )
}

export default Markups
